import { useState, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { getData } from '../../../../json-api/JsonApiUtils';
import { getTotalCount } from '../../../../utils';

export const useDataService = props => {
  const [valueList, setValueList] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const getDataHandler = useCallback(
    async (filters, resetPagination = false, pageNumber = 1, searchValue = '') => {
      const {
        dataUrl,
        dataInclude,
        pageSize,
        filterField,
        sortBy,
        authToken,
        defaultRelationData,
        queryFields,
        onDataLoaded,
      } = props;

      const nextPage = resetPagination ? 1 : pageNumber;
      setIsLoading(true);

      try {
        let query;
        if (queryFields && searchValue) {
          query = {
            name: {
              term: searchValue,
              fields: queryFields,
            },
          };
        }

        const { data, meta } = await getData(dataUrl, {
          authToken,
          includes: dataInclude,
          filters: {
            ...filters,
            ...(filterField && searchValue && !queryFields ? { [filterField]: searchValue } : undefined),
          },
          sorts: sortBy && [{ value: sortBy, direction: 'asc' }],
          page: { number: nextPage, size: pageSize },
          query,
        });

        const options = data.map(item => ({
          value: item[props.valueKey],
          label: typeof props.labelFunction === 'function' ? props.labelFunction(item) : item[props.labelKey],
          data: item,
          isDisabled: props.readOnlyList && props.readOnlyList.includes(item[props.valueKey]),
        }));

        if (defaultRelationData) {
          data.forEach(relation => {
            const key = relation.id;
            options[key] = options[key] || {};
            options[key].relation = Object.assign({}, defaultRelationData(relation, key), options[key].relation);
          });
        }

        if (onDataLoaded) {
          onDataLoaded(data);
        }

        setValueList(options);
        setTotalCount(getTotalCount(meta?.total_count || data.length));
        setIsLoading(false);

        return { data, meta };
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
        throw error;
      }
    },
    [props]
  );

  const debouncedGetDataRef = useRef(
    debounce((filters, resetPagination, pageNumber, searchValue) => {
      getDataHandler(filters, resetPagination, pageNumber, searchValue);
    }, 300)
  );

  const debouncedGetData = useCallback((filters, resetPagination, pageNumber, searchValue) => {
    debouncedGetDataRef.current(filters, resetPagination, pageNumber, searchValue);
  }, []);

  return {
    valueList,
    totalCount,
    isLoading,
    inputValue,
    setInputValue,
    getData: getDataHandler,
    debouncedGetData,
  };
};
